import { TransportItem, TransportItemType } from '@grafana/faro-web-sdk';

// Hosts of third-party scripts injected through Google Tag Manager (not part of this repo).
// RD Station's lead-tracking/forms/popup bundles are served from its CloudFront distribution and
// throw on Mobile Safari ("Invalid or unexpected token", "LeadTracking is not defined", ...).
// They accounted for ~99% of the exceptions reported to Grafana, drowning the app's own errors.
const THIRD_PARTY_SCRIPT_HOSTS = [
  'd335luupugsy2.cloudfront.net',
  'rdstation.com',
  'rdstation.com.br'
];

// Globals defined by the RD Station bundles. A ReferenceError on one of them can only come from
// RD Station code (or GTM tags calling it), never from the app, whatever the stack looks like.
// Anchored on the whole message so an app error that merely mentions one of these names is kept.
const THIRD_PARTY_GLOBAL_ERRORS = /^(?:ReferenceError: )?(?:(?:RdstationFormsIntegration|RdstationPopup|LeadTracking) is not defined|Can't find variable: (?:RdstationFormsIntegration|RdstationPopup|LeadTracking))$/;

const hostOf = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
};

const isThirdPartyHost = (host: string | null): boolean =>
  !!host && THIRD_PARTY_SCRIPT_HOSTS.some(h => host === h || host.endsWith(`.${h}`));

/**
 * Faro `beforeSend` hook: drops exceptions thrown by third-party scripts.
 *
 * Only the top stack frame (where the error was thrown) is checked, so an app error whose stack
 * merely passes through a third-party callback is still reported. Non-exception items (logs,
 * measurements, events, traces) are never touched.
 */
export function dropThirdPartyExceptions(item: TransportItem): TransportItem | null {
  if (item.type !== TransportItemType.EXCEPTION) return item;

  const payload = item.payload as { type?: string; value?: string; stacktrace?: { frames?: Array<{ filename?: string }> } };
  const topFrame = payload.stacktrace?.frames?.[0];

  if (isThirdPartyHost(hostOf(topFrame?.filename))) return null;
  if (payload.type === 'ReferenceError' && payload.value && THIRD_PARTY_GLOBAL_ERRORS.test(payload.value)) return null;

  return item;
}
